import { Component } from '@theme/component';

// Selector for focusable elements that should have tabindex managed
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Updates the tabindex of focusable elements within a slide based on its aria-hidden state.
 * When aria-hidden is true, focusable elements get tabindex="-1" to remove them from tab order.
 * When aria-hidden is false, the original tabindex is restored.
 * @param {HTMLElement} slide - The slide element to update
 * @param {boolean} isHidden - Whether the slide is hidden (aria-hidden="true")
 */
function updateFocusableElements(slide, isHidden) {
  const focusableElements = slide.querySelectorAll(FOCUSABLE_SELECTOR);

  focusableElements.forEach((element) => {
    if (isHidden) {
      // Store the original tabindex if it exists
      const currentTabindex = element.getAttribute('tabindex');
      if (currentTabindex !== '-1') {
        if (currentTabindex !== null) {
          element.dataset.originalTabindex = currentTabindex;
        }
        element.setAttribute('tabindex', '-1');
      }
    } else {
      // Restore the original tabindex or remove the attribute
      if (element.dataset.originalTabindex !== undefined) {
        element.setAttribute('tabindex', element.dataset.originalTabindex);
        delete element.dataset.originalTabindex;
      } else if (element.getAttribute('tabindex') === '-1') {
        element.removeAttribute('tabindex');
      }
    }
  });
}

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Initialize focusable elements state based on initial aria-hidden values
    this.refs.slides?.forEach((slide) => {
      const isHidden = slide.getAttribute('aria-hidden') === 'true';
      updateFocusableElements(slide, isHidden);
    });

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      const isHidden = index !== relativeIndex;
      slide.setAttribute('aria-hidden', `${isHidden}`);
      updateFocusableElements(slide, isHidden);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
